// Grouping for the New Research form's suggestions (server/suggest.ts answers them). Plain functions, no React or
// DOM, so tests can import them. Nothing here adds to a brief: the form shows these and the user clicks to add.

/** A valid hashtag, as the server checks it: letters, marks, digits and underscores, 2 to 60 of them. */
export const TAG = /^[\p{L}\p{M}\p{N}_]{2,60}$/u;
/** A hashtag as Instagram stores it: lower case, no '#', no spaces (the server's rule). */
export const tagOf = (s) => String(s ?? '').normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{M}\p{N}_]/gu, '');
const squash = (s) => String(s ?? '').normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{M}\p{N}]/gu, '');
const langName = (c) => { try { return new Intl.DisplayNames(['en'], { type: 'language' }).of(c) ?? c; } catch { return c; } };
// Tags this big are whole categories (#love, #beauty): never useful as a name for one brand or topic.
const HUGE = 1e8;

const ACCOUNT_PLATFORM = { tiktok: 'TikTok', instagram: 'Instagram', linkedin: 'LinkedIn', x: 'X', threads: 'Threads', facebook: 'Facebook' };
/** "tiktok:cetaphil" → ['tiktok', 'cetaphil']; a plain handle (every platform with accounts) → [null, 'cetaphil']. */
export function splitAccount(v) {
  const m = /^(tiktok|instagram|linkedin|x|threads|facebook):(.+)$/.exec(String(v));
  return m ? [m[1], m[2]] : [null, String(v).replace(/^@/, '')];
}
/** How an account chip reads: "@cetaphil · TikTok", or a plain handle as it was typed. */
export function accountLabel(v) {
  const [p, h] = splitAccount(v);
  return p ? `@${h} · ${ACCOUNT_PLATFORM[p]}` : String(v);
}

/**
 * Suggestions for "Other names and spellings", in display groups. Nothing already entered, and nothing twice: a
 * tag sits in the first group that offers it. An Instagram count of 0 hides a tag; a known count is kept as `ig`.
 *   names     other names: used before, what was typed before picking a Wikipedia name, Wikidata's names
 *   variants  hashtags containing a name: seen in past research, Wikidata, Instagram's related tags
 *   seen      other hashtags on those posts (generic ones too)
 *   ideas     AI ideas, except broader topics
 *   topics    broader topics: AI ideas of that kind and Instagram's related tags that do not contain a name
 */
/** Tags that say nothing about any brand or topic (server/brief.ts EMPTY_TAGS): never suggested. */
const EMPTY = new Set(['fyp', 'foryou', 'foryoupage', 'fy', 'fypage', 'viral', 'viralvideo', 'trending', 'trend', 'explore', 'explorepage', 'instagood',
  'instagram', 'tiktok', 'reels', 'reel', 'shorts', 'youtubeshorts', 'love', 'like', 'likeforlikes', 'follow', 'followme', 'photooftheday', 'xyzbca', 'capcut']);
/** Whether a suggested value names the target (an alias) or is a related topic: a hashtag must contain a plain name. */
export function namesTarget(value, name, aliases) {
  if (!String(value).trim().startsWith('#')) return true;
  const subs = [name, ...aliases.filter((a) => !a.startsWith('#'))].map(squash).filter((s) => s.length >= 3);
  return subs.some((s) => squash(value).includes(s));
}

export function aliasGroups({ name, aliases, context = [], past, entity, facts, rel, ai, checked = {} }) {
  const own = [...new Set([name, ...aliases, ...context].map(tagOf))].filter(Boolean);
  const taken = new Set(own);
  const subs = [...new Set([name, ...aliases].map(squash).filter((s) => s.length >= 3))];
  const named = (tag) => subs.some((s) => tag.includes(s));
  const out = { names: [], variants: [], seen: [], ideas: [], topics: [] };
  const add = (group, value, extra = {}) => {
    const k = tagOf(value);
    if (!k || taken.has(k) || EMPTY.has(k) || checked[k]?.posts === 0 || extra.ig === 0) return;
    taken.add(k);
    out[group].push({ value, ...extra, ig: checked[k]?.posts ?? extra.ig ?? null });
  };
  for (const a of past?.aliases ?? []) add('names', a, { source: 'used before' });
  if (entity?.via) add('names', entity.via, { source: 'what you typed' });
  if (facts?.local) add('names', facts.local.value, { source: langName(facts.local.lang) });
  for (const a of facts?.aliases ?? []) add('names', a, { source: 'Wikidata' });

  // Instagram's related tags, for the tags the user gave and the AI ideas that were checked.
  const igRelated = [...own, ...(ai?.ideas ?? []).map((i) => i.tag)].flatMap((t) => checked[t]?.related ?? []);
  for (const v of rel?.variants ?? []) add('variants', `#${v.tag}`, { posts: v.posts });
  for (const h of facts?.hashtags ?? []) add('variants', `#${h}`, { source: 'Wikidata' });
  for (const r of igRelated) if (named(r.tag)) add('variants', `#${r.tag}`, { ig: r.posts });
  for (const s of rel?.seen ?? []) add('seen', `#${s.tag}`, { posts: s.posts });
  for (const i of ai?.ideas ?? []) add(i.kind === 'related_topic' ? 'topics' : 'ideas', `#${i.tag}`, { why: i.why, kind: i.kind });
  for (const r of igRelated) if (!named(r.tag) && (r.posts ?? 0) < HUGE) add('topics', `#${r.tag}`, { ig: r.posts });
  for (const g of Object.keys(out)) out[g] = out[g].slice(0, 12);
  return out;
}

/** Words and tags to leave out, each with its reason. The name and its spellings are never offered. */
export function exclusionIdeas({ name, aliases, exclusions, past, rel, ai }) {
  const taken = new Set([name, ...aliases, ...exclusions].map(tagOf));
  const out = [];
  const add = (value, why, isAi = false) => {
    const k = tagOf(value);
    if (!k || taken.has(k)) return;
    taken.add(k);
    out.push({ value, why, ai: isAi });
  };
  for (const e of past?.exclusions ?? []) add(e, `Used before for ${past.name}`);
  for (const p of rel?.promo ?? []) add(`#${p.tag}`, `Promotional tag on ${p.posts} collected ${p.posts === 1 ? 'post' : 'posts'}`);
  for (const x of ai?.exclusions ?? []) add(x.term, x.why, true);
  return out.slice(0, 10);
}

/**
 * Accounts actually seen: in this workspace's collected posts, on Wikidata, or in earlier briefs. Never made up.
 * Platform accounts are added as "tiktok:handle" / "instagram:handle"; one already entered (a plain handle covers
 * both platforms) is not offered again.
 */
export function accountIdeas({ accounts, past, rel, facts }) {
  const entered = accounts.map(splitAccount);
  const out = [];
  const add = (platform, handle, meta) => {
    const h = handle.toLowerCase();
    const same = ([p, x]) => x.toLowerCase() === h && (!p || !platform || p === platform);
    if (entered.some(same) || out.some((o) => same([o.platform, o.handle]))) return;
    out.push({ value: platform ? `${platform}:${handle}` : handle, platform, handle, ...meta });
  };
  for (const a of rel?.accounts ?? []) add(a.platform, a.handle, { seen: a });
  for (const h of facts?.instagram ?? []) add('instagram', h, { wikidata: true });
  for (const h of facts?.tiktok ?? []) add('tiktok', h, { wikidata: true });
  for (const a of past?.accounts ?? []) { const [p, h] = splitAccount(a); add(p, h, { used: true }); }
  return out.slice(0, 8);
}

/** Up to 5 tags for one "Check on Instagram": the name, the tags entered, then AI ideas; none checked or tried before. */
export function tagsToCheck({ name, aliases, ideas, checked, tried }) {
  return [...new Set([name, ...aliases, ...ideas].map(tagOf))]
    .filter((t) => TAG.test(t) && !(t in checked) && !tried.includes(t)).slice(0, 5);
}

/** Starting points for the question, with the names filled in. */
export function questionIdeas(mode, names) {
  const [a, b] = names;
  if (!a) return [];
  if (mode === 'compare') return b ? [
    `How do people compare ${a} and ${b}?`,
    `Why do people choose ${a} over ${b}, or the other way round?`,
    `What do people like and dislike about ${a} compared with ${b}?`,
    `Which complaints come up for ${a} but not for ${b}?`,
  ] : [];
  return [
    `What do people like and dislike about ${a}?`,
    `What do people ask about ${a} before they buy?`,
    `What stops people from buying ${a}?`,
    `Do people think ${a} is worth the price?`,
  ];
}
